import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE } from '../config';

const PriestAuthContext = createContext();

const STORAGE_PRIEST = 'astroa2z_priest';
const STORAGE_TOKEN  = 'astroa2z_priest_token';

export function PriestAuthProvider({ children }) {
  const [priest, setPriest] = useState(() => {
    const saved = localStorage.getItem(STORAGE_PRIEST);
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_TOKEN));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (priest) localStorage.setItem(STORAGE_PRIEST, JSON.stringify(priest));
    else localStorage.removeItem(STORAGE_PRIEST);
  }, [priest]);

  useEffect(() => {
    if (token) localStorage.setItem(STORAGE_TOKEN, token);
    else localStorage.removeItem(STORAGE_TOKEN);
  }, [token]);

  const handle = async (url, body) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Request failed');
      setPriest(data.priest);
      if (data.token) setToken(data.token);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const login  = (identifier, password) => {
    const isEmail = identifier.includes('@');
    const body = isEmail ? { email: identifier, password } : { mobile: identifier, password };
    return handle('/api/priest/auth/login', body);
  };
  const signup = (payload)         => handle('/api/priest/auth/register', payload);

  const logout = () => {
    setPriest(null);
    setToken(null);
    setError(null);
  };

  /** Authenticated fetch wrapper for priest endpoints */
  const priestFetch = async (url, options = {}) => {
    const res = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) {
      logout();
      throw new Error('Session expired. Please login again.');
    }
    if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
    return data;
  };

  const updateProfile = async (updates) => {
    const data = await priestFetch('/api/priest/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    if (data.priest) setPriest(data.priest);
    return data;
  };

  const clearError = () => setError(null);

  return (
    <PriestAuthContext.Provider
      value={{
        priest, token, loading, error,
        login, signup, logout, clearError,
        priestFetch, updateProfile,
      }}
    >
      {children}
    </PriestAuthContext.Provider>
  );
}

export function usePriestAuth() {
  const ctx = useContext(PriestAuthContext);
  if (!ctx) throw new Error('usePriestAuth must be used within PriestAuthProvider');
  return ctx;
}
